import { createContext, useContext, useEffect } from "react";
import {
  Armchair,
  Camera,
  Eye,
  Headphones,
  KeyRound,
  Mail,
  Phone,
  Printer,
  ScanLine,
  ShieldCheck,
  Truck,
  Wrench,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import coverImage from "@/assets/flourish/flourish-mall-pod.png";
import flourishLogoLime from "@/assets/flourish/flourish-logo.png";
import flourishLogoGreen from "@/assets/flourish/flourish-logo-green.png";
import westfieldLogo from "@/assets/flourish/westfield-logo-transparent.png";
import accessCodePhoto from "@/assets/flourish/qwip-access-code-september-new.png.asset.json";
import firstUserPhoto from "@/assets/flourish/westfield-user-polaroid-latest.png.asset.json";
import shoppertainmentLogo from "@/assets/shoppertainment/shoppertainment-logo.png.asset.json";
import forumRcpLogo from "@/assets/forumrcp/forumrcp-logo.png.asset.json";
import networkRailLogo from "@/assets/networkrail/network-rail-logo.png.asset.json";
import networkRailLogoWhite from "@/assets/networkrail/network-rail-logo-white.png.asset.json";
import networkRailCover from "@/assets/networkrail/kings-cross-pod.jpg.asset.json";
import networkRailProof from "@/assets/networkrail/kings-cross-proof-polaroid.png.asset.json";

const TOTAL_PAGES = 8;
type Partner = "flourish" | "shoppertainment" | "forumrcp" | "networkrail";
const PartnerContext = createContext<Partner>("flourish");

const Page = ({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) => (
  <section className={`fl-page relative overflow-hidden ${dark ? "bg-primary text-primary-foreground" : "bg-background text-foreground"}`}>
    {children}
  </section>
);

const Lockup = ({ light = false, cover = false }: { light?: boolean; cover?: boolean }) => {
  const partner = useContext(PartnerContext);
  const isShoppertainment = partner === "shoppertainment";
  const isForumRcp = partner === "forumrcp";
  const isNetworkRail = partner === "networkrail";
  const partnerLogo = isNetworkRail ? (light ? networkRailLogoWhite.url : networkRailLogo.url) : isForumRcp ? forumRcpLogo.url : isShoppertainment ? shoppertainmentLogo.url : light ? flourishLogoLime : flourishLogoGreen;
  const partnerName = isNetworkRail ? "Network Rail" : isForumRcp ? "ForumRCP" : isShoppertainment ? "Shoppertainment" : "Flourish";

  return (
    <div className={`flex items-center ${cover ? "gap-[18px]" : "gap-[15px]"}`}>
      <span className={`font-funnel font-bold leading-none ${cover ? "text-[29px]" : "text-[24px]"} ${light ? "text-primary-foreground" : "text-foreground"}`}>qwip</span>
      <span className={`font-sans ${cover ? "text-[21px]" : "text-[18px]"} ${light ? "text-primary-foreground/35" : "text-foreground/30"}`}>×</span>
      <img
        src={partnerLogo}
        alt={partnerName}
        className={`${isNetworkRail ? (cover ? "h-[31px]" : "h-[25px]") : cover ? "h-[23px]" : "h-[19px]"} w-auto object-contain ${(isShoppertainment || isForumRcp) && light ? "brightness-0 invert" : ""}`}
      />
    </div>
  );
};

const PageHeader = ({ page, light = false }: { page: number; light?: boolean }) => (
  <header className="flex items-start justify-between">
    <Lockup light={light} />
    <p className={`font-sans text-[9px] tracking-[0.16em] ${light ? "text-primary-foreground/40" : "text-foreground/35"}`}>
      {String(page).padStart(2, "0")} / {String(TOTAL_PAGES).padStart(2, "0")}
    </p>
  </header>
);

const PageCover = () => {
  const partner = useContext(PartnerContext);
  const coverSrc = partner === "networkrail" ? networkRailCover.url : coverImage;

  return <Page dark>
    <div className="grid h-full grid-cols-[0.84fr_1.36fr]">
      <div className="fl-cover-panel relative z-10 flex flex-col justify-between px-[18mm] py-[15mm]">
        <Lockup light cover />
        <div>
          <h1 className="font-funnel text-[43px] font-bold leading-[1.02]">The modern public phone booth</h1>
          <p className="mt-6 max-w-[92mm] font-sans text-[16px] leading-relaxed text-primary-foreground/75">Bookable, soundproof pods for people working on the go.</p>
        </div>
        <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-primary-foreground/50">September 2026</p>
      </div>
      <div className="relative">
        <img src={coverSrc} alt={partner === "networkrail" ? "qwip pod at King's Cross" : "qwip pod installed in a shopping centre"} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/55 via-primary/10 to-transparent" />
      </div>
    </div>
  </Page>;
};

const facts = [
  { value: "~1 m²", label: "of floor space." },
  { value: "Self-service", label: "No staff required for operation." },
  { value: "25p/minute or £15/hour", label: "Price can be adjusted per location." },
];

const PageWhat = () => (
  <Page>
    <div className="flex h-full flex-col justify-between px-[18mm] py-[13mm]">
      <PageHeader page={2} />
      <div className="grid flex-1 grid-cols-[1.1fr_0.9fr] items-center gap-[20mm] pb-[12mm] pt-[8mm]">
        <div>
          <h2 className="font-funnel text-[46px] font-bold leading-[1.03]">A private workspace in just 1 m²</h2>
          <p className="mt-7 max-w-[135mm] font-sans text-[16px] leading-relaxed text-muted-foreground">
            Soundproof, bookable pods for calls, video meetings and focused work inside public spaces. Unlocked by QR code, charged by the minute, operated entirely by qwip.
          </p>
        </div>
        <div className="grid content-center gap-6">
          {facts.map(fact => (
            <div key={fact.value} className="border-t-2 border-primary pt-4">
              <p className="font-funnel text-[28px] font-bold leading-none">{fact.value}</p>
              <p className="mt-3 font-sans text-[15px] leading-snug text-muted-foreground">{fact.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Page>
);

const flow = [
  { icon: ScanLine, title: "Scan to book", text: "Scan the QR code to start the booking process, get an access code, and use the pod." },
  { icon: KeyRound, title: "Unlock with code", text: "A unique code unlocks the pod and remains available throughout the booking." },
  { icon: Armchair, title: "Use the pod", text: "A desk, comfortable chair and power outlets provide everything needed for focused work or a quiet moment." },
  { icon: Camera, title: "End session", text: "User leaves the pod, takes a picture, and is charged for how long they used it." },
];

const PageHowPhoto = () => (
  <Page>
    <div className="flex h-full flex-col px-[18mm] py-[13mm]">
      <PageHeader page={3} />
      <div className="mt-[8mm] grid min-h-0 flex-1 grid-cols-[0.86fr_1.14fr] overflow-hidden border border-foreground/15 bg-card shadow-[0_12px_34px_hsl(var(--primary)/0.10)]">
        <div className="relative min-h-0 overflow-hidden bg-primary">
          <img src={accessCodePhoto.url} alt="Customer entering an access code on a qwip pod" className="h-full w-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/15 via-transparent to-primary/45" />
        </div>
        <div className="flex flex-col justify-center px-[13mm] py-[9mm]">
          <h2 className="font-funnel text-[38px] font-bold leading-none">How it works</h2>
          <div className="mt-[8mm] grid gap-5">
            {flow.map((step) => (
              <div key={step.title} className="grid grid-cols-[10mm_1fr] gap-4 border-t border-foreground/15 pt-4">
                <span className="flex h-[8mm] w-[8mm] items-center justify-center bg-primary text-primary-foreground">
                  <step.icon className="h-4 w-4" />
                </span>
                <div>
                  <h3 className="font-funnel text-[19px] font-bold leading-none">{step.title}</h3>
                  <p className="mt-2 font-sans text-[14px] leading-[1.45] text-muted-foreground">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 border-t border-foreground/15 pt-4 font-sans text-[11px] italic text-muted-foreground">* Receipt or invoice provided for business expensing.</p>
        </div>
      </div>
    </div>
  </Page>
);

const testimonials = [
  { parts: [{ text: "Had a last-minute client call come up and urgently needed somewhere quiet to take it. The pod genuinely saved me!", bold: true }], name: "Aisha", role: "Consultant" },
  { parts: [{ text: "Used it for some " }, { text: "quiet study time", bold: true }, { text: " whilst my family were visiting London and shopping. Honestly, really useful." }], name: "Sophie", role: "Undergraduate Student" },
  { parts: [{ text: "I needed " }, { text: "somewhere private for a video call with my GP", bold: true }, { text: ". It was quiet, discreet and exactly what I needed. I’d definitely recommend it." }], name: "Muneeb", role: "Software Engineer" },
  { parts: [{ text: "I had " }, { text: "a university presentation to deliver online", bold: true }, { text: " and didn’t want everyone in a café hearing it, so this worked perfectly." }], name: "Jenny", role: "Postgraduate Student" },
  { parts: [{ text: "I work out at the Fitness Club downstairs, so on days I have a fitness class, " }, { text: "I don’t need to rush back to the office", bold: true }, { text: ". I can get some work and calls done right before my class." }], name: "Ebuka", role: "Freelance Designer" },
];

const WestfieldLogo = () => (
  <img src={westfieldLogo} alt="Westfield" className="h-[11mm] w-auto object-contain" />
);

const PageWestfieldImage = () => {
  const partner = useContext(PartnerContext);

  if (partner === "networkrail") {
    return (
      <Page>
        <div className="flex h-full flex-col px-[18mm] py-[13mm]">
          <PageHeader page={4} />
          <div className="mt-[7mm] grid min-h-0 flex-1 grid-cols-[1.16fr_0.84fr] overflow-hidden">
            <div className="flex min-h-0 flex-col pr-[10mm]">
              <h2 className="font-funnel text-[30px] font-bold leading-[1.02]">Loved by users at Westfield London</h2>
              <figure className="mt-[8mm] border border-[hsl(var(--testimonial))] bg-primary px-[13px] py-[11px] text-primary-foreground shadow-[5px_5px_0_hsl(var(--testimonial-shadow))]">
                <blockquote className="font-funnel text-[16px] font-semibold leading-[1.26]">“{testimonials[0].parts.map((part, i) => <span key={i}>{part.text}</span>)}”</blockquote>
                <figcaption className="mt-2 font-sans text-[9.5px] font-medium text-primary-foreground/65">{testimonials[0].name} · {testimonials[0].role}</figcaption>
              </figure>
              <div className="mt-[9px] grid grid-cols-2 gap-[9px]">
                {testimonials.slice(1).map(item => (
                  <figure key={item.name} className="flex flex-col justify-between border border-[hsl(var(--testimonial))] bg-primary px-[10px] py-[9px] text-primary-foreground shadow-[4px_4px_0_hsl(var(--testimonial-shadow))]">
                    <blockquote className="font-sans text-[10.5px] leading-[1.3]">“{item.parts.map((part, i) => <span key={i} className={part.bold ? "font-semibold" : ""}>{part.text}</span>)}”</blockquote>
                    <figcaption className="mt-2 font-sans text-[8.5px] font-medium text-primary-foreground/65">{item.name} · {item.role}</figcaption>
                  </figure>
                ))}
              </div>
              <div className="mt-[3mm] flex min-h-0 flex-1 items-start justify-between gap-[4mm] pl-[2mm]">
                <div className="shrink-0 pt-[1mm]">
                  <WestfieldLogo />
                  <span className="mt-[2mm] block text-[19px] tracking-[0.08em] text-warning" aria-hidden="true">★★★★★</span>
                  <span className="mt-[1mm] block font-sans text-[12px] font-medium text-muted-foreground" aria-label="Five stars on Google">5 stars on Google</span>
                </div>
                <img src={firstUserPhoto.url} alt="A snapshot from qwip's first user at Westfield London" className="-mt-[1mm] h-[61mm] w-[61mm] origin-center rotate-3 object-contain drop-shadow-lg" />
              </div>
            </div>
            <div className="flex min-h-0 flex-col bg-primary px-[5mm] py-[9mm] text-primary-foreground">
              <h2 className="font-funnel text-[30px] font-bold leading-[1.02]">Trialled and tested at King's Cross</h2>
              <p className="mt-[3mm] font-sans text-[13px] leading-[1.4] text-primary-foreground/75">We tested our first pod at King's Cross Station this January, with <strong className="font-bold text-primary-foreground">22 users in just one day!</strong></p>
              <img src={networkRailProof.url} alt="qwip trial at King's Cross St. Pancras" className="mt-[1mm] min-h-0 w-[calc(100%+8mm)] -translate-x-[4mm] flex-1 origin-center rotate-[4deg] scale-[1.16] object-contain object-center drop-shadow-lg" />
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return <Page>
    <div className="flex h-full flex-col px-[18mm] py-[13mm]">
      <PageHeader page={4} />
      <div className="grid min-h-0 flex-1 grid-cols-[1.56fr_0.94fr] items-stretch gap-[14mm] pb-[7mm] pt-[7mm]">
        <div>
          <h2 className="font-funnel text-[40px] font-bold leading-[1.02]">Loved by users at Westfield London</h2>
          <p className="mt-3 max-w-[150mm] font-sans text-[15px] leading-relaxed text-muted-foreground">Our first pod went live at Westfield London in June. Here’s what users have said since.</p>
          <figure className="mt-[5mm] border border-[hsl(var(--testimonial))] bg-primary p-[17px] text-primary-foreground shadow-[7px_7px_0_hsl(var(--testimonial-shadow))]">
            <blockquote className="max-w-[145mm] font-funnel text-[22px] font-semibold leading-[1.28]">“{testimonials[0].parts.map((part, i) => <span key={i} className={part.bold ? "font-semibold" : ""}>{part.text}</span>)}”</blockquote>
            <figcaption className="mt-4 font-sans text-[11px] font-medium text-primary-foreground/65">{testimonials[0].name} · {testimonials[0].role}</figcaption>
          </figure>
          <div className="mt-3 grid grid-cols-2 gap-[10px]">
            {testimonials.slice(1).map(item => (
              <figure key={item.name} className="border border-[hsl(var(--testimonial))] bg-primary p-[13px] text-primary-foreground shadow-[5px_5px_0_hsl(var(--testimonial-shadow))]">
                <blockquote className="font-sans text-[14px] leading-[1.4]">“{item.parts.map((part, i) => <span key={i} className={part.bold ? "font-semibold" : ""}>{part.text}</span>)}”</blockquote>
                <figcaption className="mt-3 font-sans text-[10.5px] font-medium text-primary-foreground/65">{item.name} · {item.role}</figcaption>
              </figure>
            ))}
          </div>
        </div>
        <div className="relative flex h-full flex-col items-start border-l border-primary pl-[14mm]">
          <WestfieldLogo />
          <span className="mt-[2mm] text-[22px] tracking-[0.08em] text-warning" aria-hidden="true">★★★★★</span>
          <span className="mt-[1mm] font-sans text-[15px] font-medium text-muted-foreground" aria-label="Five stars on Google">5 stars on Google</span>
          <img src={firstUserPhoto.url} alt="A snapshot from qwip's first user at Westfield London" className="mt-[4mm] max-h-[156mm] max-w-full origin-bottom-left rotate-3 object-contain" />
        </div>
      </div>
    </div>
  </Page>;
};

const operations = [
  {
    icon: Truck,
    title: "Deliver and install",
    text: "2-week lead time, delivered across the UK, installed in under an hour.",
  },
  {
    icon: Headphones,
    title: "Support",
    text: "24/7 customer support available from the qwip team.",
  },
  {
    icon: Wrench,
    title: "Maintain",
    text: "Daily cleaning, regular maintenance and repairs managed by the qwip team.",
  },
];

const PageOperations = () => (
  <Page>
    <div className="flex h-full flex-col px-[18mm] py-[13mm]">
      <PageHeader page={5} />
      <div className="flex flex-1 flex-col justify-center pb-[10mm]">
        <h2 className="max-w-[220mm] font-funnel text-[43px] font-bold leading-[1.05]">Installed in under an hour. Fully managed by qwip.</h2>
        <div className="mt-[16mm] grid grid-cols-3 gap-6">
          {operations.map((item, index) => (
            <div key={item.title} className="min-h-[76mm] border border-foreground/15 p-7">
              <div className="flex items-center justify-between">
                <item.icon className="h-7 w-7 text-primary" />
                <span className="font-sans text-[9px] text-foreground/35">0{index + 1}</span>
              </div>
              <h3 className="mt-10 font-funnel text-[21px] font-bold">{item.title}</h3>
              <p className="mt-4 font-sans text-[16px] leading-relaxed text-muted-foreground">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Page>
);

const security = [
  { icon: Eye, title: "Visible, with screen privacy", text: "The occupant and their belongings remain partially visible from outside, whilst user screen privacy is maintained." },
  { icon: KeyRound, title: "Controlled access", text: "Session and access activity is logged, with any issues reviewed by the qwip team." },
  { icon: Camera, title: "Checked after use", text: "An end-of-session photo helps qwip identify and retain anything left behind or evidence of misuse." },
  { icon: ShieldCheck, title: "Managed response", text: "Damage, forced entry or unattended items are handled by qwip and escalated to centre security." },
];

const PageSecurity = () => {
  const partner = useContext(PartnerContext);
  return <Page dark>
    <div className="flex h-full flex-col px-[18mm] py-[13mm]">
      <PageHeader page={6} light />
      <div className="flex flex-1 flex-col justify-center pb-[8mm]">
        <h2 className="max-w-[210mm] font-funnel text-[40px] font-bold leading-[1.05]">Secure for customers and the {partner === "networkrail" ? "station" : "centre"}</h2>
        <div className="mt-[10mm] grid grid-cols-2 gap-x-[14mm] gap-y-8">
          {security.map((block, index) => (
            <div key={block.title} className="border-t border-primary-foreground/25 pt-4">
              <div className="flex items-center justify-between">
                <block.icon className="h-5 w-5 text-primary-foreground/70" />
                <span className="font-sans text-[8px] text-primary-foreground/40">0{index + 1}</span>
              </div>
              <h3 className="mt-4 font-funnel text-[20px] font-bold">{block.title}</h3>
              <p className="mt-3 font-sans text-[15px] leading-relaxed text-primary-foreground/70">{block.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Page>;
};

const standardCommercialTerms = [
  { value: "£0", label: "upfront cost to the centre" },
  { value: "20%", label: "of booking revenue, paid to the centre" },
  { value: "6 months", label: "pilot term" },
];

const PageCommercial = () => {
  const partner = useContext(PartnerContext);
  const commercialTerms =
    partner === "shoppertainment"
      ? [
          { value: "£0", label: "upfront cost to the centre" },
          { value: "Fixed rent / revenue", label: "paid to centre", sub: "specifics based on location" },
          { value: "6 months", label: "pilot term" },
        ]
      : partner === "forumrcp"
        ? [
            { value: "£0", label: "upfront cost to the centre" },
            { value: "Fixed rent or revenue share", label: "paid to the centre" },
            { value: "3-6 months", label: "pilot term" },
          ]
        : standardCommercialTerms;

  return (
    <Page>
      <div className="flex h-full flex-col px-[18mm] py-[13mm]">
        <PageHeader page={7} />
        <div className="flex flex-1 flex-col justify-center pb-[7mm]">
          <h2 className="text-center font-funnel text-[44px] font-bold leading-none">Our pilot proposal</h2>
          <div className="mt-[12mm] grid grid-cols-3 gap-8">
            {commercialTerms.map(term => (
              <div key={term.value} className="border-y border-foreground/15 py-7 text-center">
                <p className={`font-funnel font-bold leading-none text-primary ${term.value.length > 12 ? "text-[34px]" : "text-[49px]"}`}>{term.value}</p>
                <p className="mx-auto mt-4 max-w-[72mm] font-sans text-[16px] font-medium leading-snug text-muted-foreground">{term.label}</p>
                {term.sub && (
                  <p className="mx-auto mt-1 max-w-[72mm] font-sans text-[13px] leading-snug text-muted-foreground/70">{term.sub}</p>
                )}
              </div>
            ))}
          </div>
          {partner === "networkrail" ? (
            <p className="mx-auto mt-[12mm] max-w-[220mm] text-center font-sans text-[23px] leading-[1.35] text-primary">Turn <strong className="font-bold">time between journeys</strong>, into <strong className="font-bold">time well spent.</strong></p>
          ) : (
            <p className="mx-auto mt-[12mm] max-w-[220mm] text-center font-sans text-[23px] leading-[1.35] text-primary">Give your customers <strong className="font-bold">one less reason to leave</strong>, and <strong className="font-bold">one more reason to stay</strong>.</p>
          )}
        </div>
      </div>
    </Page>
  );
};

const PageContact = () => (
  <Page dark>
    <div className="fl-contact-panel flex h-full flex-col px-[18mm] py-[15mm]">
      <header className="flex items-start justify-between">
        <Lockup light cover />
        <p className="font-sans text-[9px] tracking-[0.16em] text-primary-foreground/40">08 / 08</p>
      </header>
      <div className="flex flex-1 flex-col justify-center pb-[8mm]">
        <p className="font-funnel text-[58px] font-bold leading-none">qwip</p>
        <p className="mt-6 max-w-[185mm] font-funnel text-[34px] font-semibold leading-[1.12] text-primary-foreground/80">Let’s bring the future of work to your space.</p>
        <div className="mt-[14mm] flex gap-[18mm] border-t border-primary-foreground/20 pt-7">
          <a href="mailto:hello@qwip.co.uk" className="flex items-center gap-3 font-sans text-[16px] text-primary-foreground"><Mail className="h-5 w-5" />hello@qwip.co.uk</a>
          <a href="tel:+447347474059" className="flex items-center gap-3 font-sans text-[16px] text-primary-foreground"><Phone className="h-5 w-5" />+44 7347 474059</a>
        </div>
      </div>
    </div>
  </Page>
);

const pages = [PageCover, PageWhat, PageHowPhoto, PageWestfieldImage, PageOperations, PageSecurity, PageCommercial, PageContact];

const FlourishDeck = ({ autoPrint = false, partner = "flourish" }: { autoPrint?: boolean; partner?: Partner }) => {
  useEffect(() => {
    document.title = partner === "shoppertainment"
      ? "qwip × Shoppertainment · shopping centre pilot proposal"
      : partner === "forumrcp"
        ? "qwip × ForumRCP · shopping centre pilot proposal"
        : partner === "networkrail"
          ? "qwip × Network Rail · station pilot proposal"
          : "qwip × Flourish · shopping centre pilot proposal";
  }, [partner]);
  useEffect(() => { if (!autoPrint) return; const timer = window.setTimeout(() => window.print(), 2000); return () => window.clearTimeout(timer); }, [autoPrint]);
  return (
    <PartnerContext.Provider value={partner}>
    <main className="fl-doc min-h-screen bg-muted py-[14mm]">
      <style>{`
        .fl-page { width: 297mm; height: 210mm; margin: 0 auto; position: relative; overflow: hidden; }
        .fl-cover-panel { background:linear-gradient(145deg,hsl(var(--primary)) 0%,hsl(var(--foreground)) 72%,hsl(var(--primary) / .9) 100%); box-shadow:inset -22mm 0 30mm hsl(var(--foreground) / .14); }
        .fl-contact-panel { background:linear-gradient(145deg,hsl(var(--primary)) 0%,hsl(var(--foreground)) 70%,hsl(var(--primary) / .9) 100%); }
        @media screen { .fl-doc { display:flex; flex-direction:column; align-items:center; gap:10mm; } .fl-page { box-shadow:0 10px 40px hsl(var(--primary) / .18); } }
        @media print { @page { size:A4 landscape; margin:0; } html,body,#root { margin:0!important; padding:0!important; min-height:0!important; background:hsl(var(--background))!important; -webkit-print-color-adjust:exact!important; print-color-adjust:exact!important; } .fl-doc { display:block!important; padding:0!important; gap:0!important; background:hsl(var(--background))!important; } .fl-no-print { display:none!important; } .fl-page { box-shadow:none!important; margin:0!important; break-inside:avoid; page-break-inside:avoid; break-after:page; page-break-after:always; } .fl-page:last-child { break-after:auto; page-break-after:auto; } }
      `}</style>
      <div className="fl-no-print fixed bottom-6 right-6 z-50">
        <Button onClick={() => window.print()}><Printer />Save as PDF</Button>
      </div>
      {pages.map((ProposalPage, index) => <ProposalPage key={index} />)}
    </main>
    </PartnerContext.Provider>
  );
};

export default FlourishDeck;
